"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const floatingCards = [
{
  id: "card-1",
  delay: "float-gentle",
  position: "top-[12%] right-[8%]",
  content: {
    label: "NFC Tag #TT-4821",
    title: "1968 Gibson Les Paul",
    sub: "Signed by Jimmy Page",
    badge: "Authenticated",
    badgeColor: "text-accent-emerald"
  }
},
{
  id: "card-2",
  delay: "float-gentle-delay",
  position: "bottom-[18%] right-[5%]",
  content: {
    label: "Tap to Explore · 8 Markers",
    title: "Provenance Verified",
    sub: "Christie's Auction, 1994",
    badge: "3 Markers",
    badgeColor: "text-accent-purple"
  }
},
{
  id: "card-3",
  delay: "float-gentle-delay-2",
  position: "bottom-[32%] left-[3%] hidden xl:block",
  content: {
    label: "Collection",
    title: "47 Active Tags",
    sub: "Music Memorabilia",
    badge: "Collector Plan",
    badgeColor: "text-accent-gold"
  }
}];


export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const scrollY = window.scrollY;
      const parallaxEl = sectionRef.current.querySelector(".parallax-slow") as HTMLElement;
      if (parallaxEl) {
        parallaxEl.style.transform = `translateY(${scrollY * 0.15}px)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen pt-24 pb-16 px-6 md:px-8 flex items-end overflow-hidden bg-bg-primary"
      aria-label="Hero section">
      
      {/* Background gradient */}
      <div className="absolute inset-0 bg-hero-gradient pointer-events-none" />
      <div className="absolute inset-0 grid-lines pointer-events-none opacity-40" />

      {/* Ambient glow blobs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-accent-purple/8 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-accent-crimson/6 blur-[80px] pointer-events-none" />

      <div className="relative z-10 max-w-container mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end min-h-[calc(100vh-6rem)]">
          
          {/* Left: Text + CTA */}
          <div className="lg:col-span-5 flex flex-col justify-center pb-8 lg:pb-16 order-2 lg:order-1">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 mb-6 animate-fade-in-up">
              <span className="nfc-pulse w-2 h-2 rounded-full bg-accent-crimson" />
              <span className="font-mono text-[11px] text-gray-400 uppercase tracking-[0.3em]">
                NFC · QR · Digital Cataloging
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[0.92] tracking-tight text-white mb-6 animate-fade-in-up"
              style={{ animationDelay: "0.1s" }}>
              
              Your Collection
              <br />
              <span className="italic text-gray-400">Deserves More</span>
              <br />
              Than a Shelf.
            </h1>

            <p
              className="text-gray-400 text-base leading-relaxed max-w-sm border-l border-accent-crimson/40 pl-5 mb-8 animate-fade-in-up"
              style={{ animationDelay: "0.2s" }}>

              Your unique and irreplaceable memorabilia hold stories worth sharing. All it takes is a TapTale tag to share interactive galleries, provenance, signatures, and the stories behind each piece — with friends, family, the world — or keep it private. <br/><span className="text-white font-medium">One tag. One tap. A lifetime of stories.</span>
            </p>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row gap-3 mb-12 animate-fade-in-up"
              style={{ animationDelay: "0.3s" }}>
              
              <Link
                href="/pricing"
                className="btn-lift inline-flex items-center justify-center gap-2 bg-accent-crimson hover:bg-accent-crimson-hover text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-colors">
                
                <span>Get Your Starter Kit</span>
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
              <Link
                href="#how-it-works"
                className="group flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors duration-300">
                <Icon name="PlayCircleIcon" size={16} />
                <span>See How It Works</span>
              </Link>
            </div>
          </div>

          {/* Right: Arch Hero Image with Floating Cards */}
          <div className="lg:col-span-7 relative h-[60vh] lg:h-[88vh] order-1 lg:order-2">
            {/* Rotating NFC Badge */}
            <div className="absolute top-6 left-8 z-20 pointer-events-none hidden lg:block">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <svg className="animate-rotate-badge w-full h-full" viewBox="0 0 100 100">
                  <path
                    id="circlePath-hero"
                    d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
                    fill="transparent" />
                  
                  <text fontSize="10.5" fontFamily="JetBrains Mono" fontWeight="500" letterSpacing="2.5px" fill="rgba(255,255,255,0.5)">
                    <textPath href="#circlePath-hero" startOffset="0%">
                      NFC ENABLED · TAP TO REVEAL ·
                    </textPath>
                  </text>
                </svg>
                <div className="absolute w-6 h-6 rounded-full bg-accent-crimson/20 border border-accent-crimson/50 flex items-center justify-center nfc-pulse">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-crimson" />
                </div>
              </div>
            </div>

            {/* Main Arch Image */}
            <div className="arch-shape w-full h-full overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.6)] parallax-slow">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1eb82d277-1771515382035.png"
                alt="Vintage signed guitar displayed as a premium collectible with dramatic lighting"
                fill
                className="object-cover object-center hover:scale-105 transition-transform duration-[2s]"
                priority />
              
              {/* Dark vignette overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            </div>

            {/* Floating Metadata Cards */}
            {floatingCards.map((card) =>
            <div
              key={card.id}
              className={`absolute ${card.position} z-20 ${card.delay}`}>
              
                <div className="glass-card-dark rounded-2xl p-4 min-w-[180px] shadow-glass">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="nfc-pulse w-1.5 h-1.5 rounded-full bg-accent-crimson" />
                    <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest">
                      {card.content.label}
                    </span>
                  </div>
                  <p className="text-white text-xs font-semibold leading-snug mb-0.5">
                    {card.content.title}
                  </p>
                  <p className="text-gray-500 text-[10px] mb-2">
                    {card.content.sub}
                  </p>
                  <span className={`font-mono text-[9px] font-semibold uppercase tracking-wider ${card.content.badgeColor}`}>
                    {card.content.badge}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40 animate-bounce">
        <Icon name="ChevronDownIcon" size={20} className="text-white" />
      </div>
    </section>);

}